import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

import OidcSilentRenewNavigator from './oidcSilentRenewNavigator';
import HOSTNAMES from '../util-helpers/environmentUtils';

type Environment = 'LOCAL' | 'NONPROD' | 'US' | 'CA' | 'CJIS';

interface FVIDSettings {
    STS_AUTHORITY: string;
    CLIENT_ID: string;
    API_BASE: string;
}

interface EnvironmentSettings {
    LOCAL: FVIDSettings;
    NONPROD: FVIDSettings;
    US: FVIDSettings;
    CA: FVIDSettings;
    CJIS: FVIDSettings;
    [key: string]: FVIDSettings;
}

export const ENV_SETTINGS: EnvironmentSettings = {
    LOCAL: {
        STS_AUTHORITY: 'https://identity-nonprod-public.filevinedev.com',
        CLIENT_ID: '31ec7f87-0922-4ee5-b928-888f73666c37',
        API_BASE: 'https://team-ext0.filevinedev.com/v2',
    },
    NONPROD: {
        STS_AUTHORITY: 'https://identity-nonprod-public.filevinedev.com',
        CLIENT_ID: '31ec7f87-0922-4ee5-b928-888f73666c37',
        API_BASE: 'https://api.filevinedev.com/fv-app/v2',
    },
    US: {
        STS_AUTHORITY: 'https://identity.filevine.com',
        CLIENT_ID: '9afe7234-bd2d-421c-9ab7-43bdc413ab84',
        API_BASE: 'https://api.filevineapp.com/fv-app/v2',
    },
    CA: {
        STS_AUTHORITY: 'https://identity.filevine.ca',
        CLIENT_ID: '69715aa3-908d-41d8-87a8-2a240cabde7f',
        API_BASE: 'https://api.filevineapp.ca/fv-app/v2',
    },
    CJIS: {
        STS_AUTHORITY: 'https://identity.filevinegov.com',
        CLIENT_ID: '3cb5f4fe-57a4-443e-8e40-3267fecae134',
        API_BASE: 'https://api.filevinegov.com/fv-app/v2',
    },
};

export const getEnvironment = (): Environment | null => {
    // Production Environments
    if (window.location.hostname === HOSTNAMES.US_PROD) {
        return 'US';
    }
    if (window.location.hostname === HOSTNAMES.CA_PROD) {
        return 'CA';
    }
    if (window.location.hostname === HOSTNAMES.CJIS_PROD) {
        return 'CJIS';
    }

    // Dev Ephemeral Stable Environments
    if (
        window.location.hostname === HOSTNAMES.US_DEV
        || window.location.hostname.includes(HOSTNAMES.EPHEMERAL_PATTERN)
        || window.location.hostname === HOSTNAMES.US_STABLE
    ) {
        return 'NONPROD';
    }

    // Local Environment
    if (window.location.hostname === HOSTNAMES.LOCAL) {
        return 'LOCAL';
    }

    return null;
};

const isModernBrowserRuntime = (): boolean => {
    // Check if we're in web version or modern Office apps -- excluding mac
    if (
        typeof Office !== 'undefined'
        && Office.context
        && Office.context.platform
    ) {
        // Office.PlatformType.OfficeOnline indicates web version (all Office apps)
        // Office.PlatformType.Universal indicates modern Office apps (UWP)
        return (
            Office.context.platform === Office.PlatformType.OfficeOnline
            || Office.context.platform === Office.PlatformType.Universal
        );
    }

    // Fallback: check user agent for modern browsers
    const userAgent = navigator.userAgent.toLowerCase();
    return (
        userAgent.includes('chrome')
        || userAgent.includes('firefox')
        || userAgent.includes('safari')
        || userAgent.includes('edge')
        || userAgent.includes('webkit')
    ); // Additional check for modern WebKit-based browsers
};

const createSettings = (environment: Environment | null) => {
    if (!environment) {
        throw new Error('No environment detected');
    }

    const baseSettings = {
        authority: ENV_SETTINGS[environment].STS_AUTHORITY,
        client_id: ENV_SETTINGS[environment].CLIENT_ID,
        redirect_uri: `${window.location.origin}/signin.html`,
        response_type: 'code',
        scope: 'openid profile email phone tenant filevine.v2.api.* theme fv.vitals.api.* fv.api.gateway.access fv.auth.tenant.read offline_access',
        post_logout_redirect_uri: `${window.location.origin}/signout.html`,
        silent_redirect_uri: `${window.location.origin}/silent-renew.html`,
        automaticSilentRenew: true,
    };

    // Use sessionStorage for modern browser runtimes, localStorage for legacy (classic outlook, office desktop, and mac versions)
    const storage = isModernBrowserRuntime()
        ? window.sessionStorage
        : window.localStorage;

    return {
        ...baseSettings,
        userStore: new WebStorageStateStore({ store: storage }),
    };
};

export const userManager = new UserManager(
    createSettings(getEnvironment()),
    undefined,
    undefined,
    new OidcSilentRenewNavigator(),
);
